import { useState } from "react";
import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { cn } from "@/lib/cn";
import { formatCurrency, formatNumber, formatPercentRaw, safeRatio } from "@/lib/format";
import { usePayload } from "@/lib/usePayload";

export function LinkedInCampaigns() {
  const { payload, isLoading, monthLabel } = usePayload();
  const [filterMode, setFilterMode] = useState<"active" | "all">("active");

  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;
  
  const rawCampaigns = payload.linkedin_campaigns || [];
  
  const activeCount = rawCampaigns.filter((c: any) => (c.status || "").toUpperCase() === "ACTIVE").length;
  const campaigns = filterMode === "active" 
    ? rawCampaigns.filter((c: any) => (c.status || "").toUpperCase() === "ACTIVE" || (c.spend && c.spend > 0))
    : rawCampaigns;

  const summary = payload.linkedin_summary || {
    active_campaigns: activeCount,
    total_spend: rawCampaigns.reduce((s: number, c: any) => s + (c.spend || 0), 0),
    total_clicks: rawCampaigns.reduce((s: number, c: any) => s + (c.clicks || 0), 0),
    total_impressions: rawCampaigns.reduce((s: number, c: any) => s + (c.impressions || 0), 0),
    total_conversions: rawCampaigns.reduce((s: number, c: any) => s + (c.conversions || 0), 0)
  };

  const totalImpressions = summary.total_impressions || rawCampaigns.reduce((s: number, c: any) => s + (c.impressions || 0), 0);
  const totalClicks = summary.total_clicks || rawCampaigns.reduce((s: number, c: any) => s + (c.clicks || 0), 0);
  const avgCtr = totalImpressions ? (totalClicks / totalImpressions) * 100 : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">LinkedIn Campaigns</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            LinkedIn Sponsored Content & Lead Gen campaign performance — {monthLabel}
          </p>
        </div>
        <div className="inline-flex items-center rounded-lg border border-border bg-slate-100/80 p-0.5 text-xs font-medium">
          <button
            type="button"
            onClick={() => setFilterMode("active")}
            className={cn(
              "px-3 py-1.5 rounded-md transition-colors",
              filterMode === "active"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            Active & With Spend ({activeCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode("all")}
            className={cn(
              "px-3 py-1.5 rounded-md transition-colors",
              filterMode === "all"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            All Campaigns ({rawCampaigns.length})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard accent="blue" label="Active Campaigns" value={formatNumber(activeCount)} />
        <KpiCard accent="green" label="Total Spend" value={formatCurrency(summary.total_spend, { compact: true })} />
        <KpiCard accent="cyan" label="Total Clicks" value={formatNumber(totalClicks)} />
        <KpiCard
          accent="amber"
          label="Avg CTR"
          value={avgCtr != null ? formatPercentRaw(avgCtr / 100) : "—"}
        />
      </div>

      <Panel
        title={filterMode === "active" ? "Active LinkedIn Campaigns" : "LinkedIn Campaign Details"}
        right={
          <span className="pill bg-blue-50 border border-blue-200 text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            <span className="numeric-value font-semibold">{formatNumber(campaigns.length)}</span> Campaigns
          </span>
        }
      >
        {campaigns.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            No active LinkedIn campaigns for this period.
          </div>
        ) : (
          <CampaignTable campaigns={campaigns} />
        )}
      </Panel>
    </div>
  );
}

function CampaignTable({ campaigns }: { campaigns: any[] }) {
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Campaign</th>
            <th className="font-medium px-3 py-2.5">Status</th>
            <th className="font-medium px-3 py-2.5 text-right">Spend</th>
            <th className="font-medium px-3 py-2.5 text-right">Impressions</th>
            <th className="font-medium px-3 py-2.5 text-right">Clicks</th>
            <th className="font-medium px-3 py-2.5 text-right">Conversions</th>
            <th className="font-medium px-3 py-2.5 text-right">CPC</th>
            <th className="font-medium px-5 py-2.5 text-right">CTR</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const isActive = (c.status || "").toUpperCase() === "ACTIVE";
            const ctr = c.ctr ?? safeRatio(c.clicks, c.impressions);
            return (
              <tr key={c.campaign_name} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium">{c.campaign_name}</td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                    <span className={isActive ? "text-green-700" : "text-slate-500"}>
                      {isActive ? "Active" : c.status || "Paused"}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatCurrency(c.spend, { compact: true })}</td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.impressions)}</td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.clicks)}</td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.conversions)}</td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.cpc ? formatCurrency(c.cpc, { compact: true }) : "—"}
                </td>
                <td className="px-5 py-2.5 text-right numeric-value">
                  {ctr != null ? formatPercentRaw(ctr / 100) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
